import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { StatsGrid, StatCard, BarChartWidget, LineChartWidget, PieChartWidget, AnalyticsGrid, generateMockData } from '../components/Charts';
import CreditTransferModal from '../components/CreditTransfer';

const api = axios.create( {
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
} );

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function PAASAdminDashboard({ view }) {
  const { paasId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();

  const [activeSubMenu, setActiveSubMenu] = useState(view || 'all-saas');

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'saas', label: 'SaaS Systems', icon: '🏢', hasSubMenu: true },
    { id: 'packages', label: 'Packages', icon: '📦', hasSubMenu: true },
    { id: 'analytics', label: 'Analytics', icon: '📈', hasSubMenu: true },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'income', label: 'Income', icon: '💰' },
    { id: 'transfer', label: 'Transfer', icon: '💸' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const saasSubMenus = [
    { id: 'all-saas', label: 'All SaaS Systems' },
    { id: 'create-saas', label: 'Create SaaS System' },
    { id: 'saas-admins', label: 'SaaS Admins' },
  ];

  const packagesSubMenus = [
    { id: 'all-packages', label: 'All Packages' },
    { id: 'add-package', label: 'Add Package' },
    { id: 'package-usage', label: 'Package Usage' },
  ];

  const analyticsSubMenus = [
    { id: 'overview', label: 'Overview' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'usage', label: 'Usage Stats' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    dispatch({ type: 'auth/logout' });
    navigate('/login');
  };
  const [saas, setPaas] = useState(null);
  const [saasList, setPaasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    domain: '',
    subscription: { plan: 'free' },
    companyName: '',
    description: '',
    primaryColor: '#7C3AED',
    termsUrl: '',
    privacyUrl: '',
    supportEmail: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    logo: '',
    logoFile: null,
    logoFilePreview: null,
    clientAdminEmail: '',
    clientAdminPassword: ''
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsData, setSettingsData] = useState({
    name: '',
    description: '',
    domain: '',
    contactEmail: '',
    primaryColor: '#7C3AED',
    termsUrl: '',
    privacyUrl: '',
    supportEmail: '',
    saasAdminEmail: '',
    saasAdminPassword: ''
  });
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [incomeData, setIncomeData] = useState(null);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [saasAdmins, setSaasAdmins] = useState([]);
  const [saasAdminsLoading, setSaasAdminsLoading] = useState(false);

  useEffect(() => {
    if (paasId) {
      fetchSaaS();
      fetchPackages();
      fetchUsageData();
    }
  }, [paasId]);

  useEffect(() => {
    if (activeTab === 'saas' && activeSubMenu === 'saas-admins') {
      fetchSaasAdmins();
    }
  }, [activeTab, activeSubMenu]);

  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (path.includes('/saas/create')) {
      setActiveTab('saas');
      setActiveSubMenu('create-saas');
    } else if (path.includes('/saas/admins')) {
      setActiveTab('saas');
      setActiveSubMenu('saas-admins');
    } else if (path.includes('/saas')) {
      setActiveTab('saas');
      setActiveSubMenu('all-saas');
    } else if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.pathname, location.search]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollSaaS = setInterval(() => {
      fetchSaaS();
    }, 10000); // 10 seconds

    const pollBilling = setInterval(() => {
      fetchBillingData();
    }, 15000); // 15 seconds

    const pollSaaSAdmins = setInterval(() => {
      if (activeTab === 'saas' && activeSubMenu === 'saas-admins') {
        fetchSaasAdmins();
      }
    }, 15000); // 15 seconds

    const pollPackages = setInterval(() => {
      fetchPackages();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(pollSaaS);
      clearInterval(pollBilling);
      clearInterval(pollSaaSAdmins);
      clearInterval(pollPackages);
    };
  }, [activeTab, activeSubMenu]);

  const fetchSaasAdmins = async () => {
    try {
      setSaasAdminsLoading(true);
      const res = await api.get('/users/saas-admins', { params: { paasId } });
      setSaasAdmins(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch SaaS admins:', error);
      setSaasAdmins([]);
    } finally {
      setSaasAdminsLoading(false);
    }
  };

  const fetchSaaS = async () => {
    try {
      const res = await api.get(`/paas/${paasId}`);
      setPaas(res.data.data);
      const tenantRes = await api.get(`/paas/${paasId}/saas`);
      setPaasList(tenantRes.data.data || []);
      fetchBillingData();
    } catch (error) {
      console.error('Failed to fetch SaaS:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      const [balanceRes, txRes, invRes] = await Promise.all([
        api.get('/billing/balance'),
        api.get('/billing/transactions?limit=10'),
        api.get('/billing/invoices?limit=5')
      ]);
      setBalance(balanceRes.data.data);
      setTransactions(txRes.data.data.transactions || []);
      setInvoices(invRes.data.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleTopup = async (method) => {
    try {
      const amount = formData.topupAmount || 5000;
      let paymentMethod = method || 'razorpay';

      if (paymentMethod === 'razorpay') {
        const orderRes = await api.post('/billing/pay/razorpay/create-order', { amount });
        alert(`Razorpay Order created: ${orderRes.data.data.orderId}. Amount: ₹${amount}`);
      } else if (paymentMethod === 'stripe') {
        const intentRes = await api.post('/billing/pay/stripe/create-intent', { amount });
        alert(`Stripe Payment Intent created. Amount: ₹${amount}`);
      }

      await api.post(`/billing/balance/${saas?.tenantId || 'default'}/recharge`, { amount, paymentMethod });
      alert(`Successfully recharged ₹${amount} credits!`);
      setShowTopupModal(false);
      fetchBillingData();
    } catch (error) {
      alert('Failed to top up: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchIncomeData = async () => {
    try {
      setIncomeLoading(true);
      const res = await api.get('/billing/income');
      setIncomeData(res.data.data);
    } catch (error) {
      console.error('Failed to fetch income data:', error);
    } finally {
      setIncomeLoading(false);
    }
  };

  const handleTransfer = async () => {
    try {
      if (!formData.transferTenantId || !formData.transferAmount) {
        alert('Please select a client and enter an amount');
        return;
      }

      await api.post('/billing/transfer', {
        fromTenantId: saas?.tenantId || 'saas-admin',
        toTenantId: formData.transferTenantId,
        amount: parseFloat(formData.transferAmount),
        description: formData.transferDescription || 'Credit transfer to client'
      });

      alert(`Successfully transferred ₹${formData.transferAmount} credits!`);
      setFormData({ ...formData, transferTenantId: '', transferAmount: '', transferDescription: '' });
      fetchBillingData();
    } catch (error) {
      alert('Failed to transfer: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);
      const res = await api.get('/billing/analytics');
      setAnalyticsData(res.data.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const res = await api.get('/packages/platform');
      setPackages(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const res = await api.get('/usage/stats', {
        headers: { 'x-paas-id': paasId }
      });
      setUsageData(res.data.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const isFileUpload = formData.logoFile;

      let payload;
      if (isFileUpload) {
        payload = new FormData();
        payload.append('name', formData.name);
        payload.append('contactEmail', formData.contactEmail);
        payload.append('domain', formData.domain);
        payload.append('subscription', JSON.stringify({ plan: formData.subscription.plan }));
        payload.append('companyName', formData.companyName);
        payload.append('description', formData.description);
        payload.append('primaryColor', formData.primaryColor);
        payload.append('termsUrl', formData.termsUrl);
        payload.append('privacyUrl', formData.privacyUrl);
        payload.append('supportEmail', formData.supportEmail);
        payload.append('address', formData.address);
        payload.append('city', formData.city);
        payload.append('state', formData.state);
        payload.append('country', formData.country);
        payload.append('zipCode', formData.zipCode);
        payload.append('logoFile', formData.logoFile);
        payload.append('clientAdminEmail', formData.clientAdminEmail);
        payload.append('clientAdminPassword', formData.clientAdminPassword);
      } else {
        payload = {
          name: formData.name,
          contactEmail: formData.contactEmail,
          domain: formData.domain,
          subscription: { plan: formData.subscription.plan },
          companyName: formData.companyName,
          description: formData.description,
          primaryColor: formData.primaryColor,
          termsUrl: formData.termsUrl,
          privacyUrl: formData.privacyUrl,
          supportEmail: formData.supportEmail,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zipCode: formData.zipCode,
          logo: formData.logo,
          clientAdminEmail: formData.clientAdminEmail,
          clientAdminPassword: formData.clientAdminPassword
        };
      }

      await api.post(`/paas/${paasId}/saas`, payload);
      setShowTenantModal(false);
      fetchSaaS();
      setFormData({
        name: '', contactEmail: '', domain: '', subscription: { plan: 'free' },
        companyName: '', description: '', primaryColor: '#7C3AED',
        termsUrl: '', privacyUrl: '', supportEmail: '',
        address: '', city: '', state: '', country: '', zipCode: '', logo: '',
        logoFile: null, logoFilePreview: null,
        clientAdminEmail: '', clientAdminPassword: ''
      });
      alert('Client created successfully!');
    } catch (error) {
      alert('Failed to create client: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setFormData({ ...formData, logoFile: file, logoFilePreview: preview });
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Delete this tenant?')) return;
    try {
      await api.delete(`/saas/${tenantId}`);
      fetchSaaS();
    } catch (error) {
      alert('Failed to delete tenant');
    }
  };

  const handleStatusToggle = async (tenantId, currentStatus) => {
    try {
      await api.patch(`/saas/${tenantId}/status`, { isActive: !currentStatus });
      fetchSaaS();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  useEffect(() => {
    if (saas) {
      setSettingsData({
        name: saas.name || '',
        description: saas.description || '',
        domain: saas.domain || '',
        contactEmail: saas.contactEmail || '',
        primaryColor: saas.primaryColor || '#7C3AED',
        termsUrl: saas.termsUrl || '',
        privacyUrl: saas.privacyUrl || '',
        supportEmail: saas.supportEmail || '',
        saasAdminEmail: saas.saasAdminEmail || '',
        saasAdminPassword: ''
      });
    }
  }, [saas]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...settingsData };
      if (!settingsData.saasAdminPassword) {
        delete payload.saasAdminPassword;
      }
      await api.patch(`/paas/${paasId}`, payload);
      setShowSettingsModal(false);
      fetchSaaS();
      alert('Settings saved!');
    } catch (error) {
      alert('Failed to save settings: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar */ }
      <div className="w-64 bg-white shadow-lg flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold" style={{ color: saas?.primaryColor || '#7C3AED' }}>
            {saas?.name || 'PaaS Admin'}
          </h2>
          <p className="text-sm text-gray-500">System Dashboard</p>
        </div>
        <nav className="flex-1 p-4">
          {menuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.hasSubMenu) {
                    setActiveTab(item.id);
                    setActiveSubMenu('all-saas');
                  } else {
                    setActiveTab(item.id);
                  }
                }}
                className={`w-full text-left px-4 py-3 rounded-lg mb-1 flex items-center gap-3 ${activeTab === item.id
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                <span>{item.icon}</span>
                <span className="font-medium">{item.label}</span>
                {item.hasSubMenu && <span className="ml-auto text-xs">▼</span>}
              </button>
              {item.id === 'saas' && activeTab === 'saas' && (
                <div className="ml-4 mt-1 mb-3">
                  {saasSubMenus.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubMenu(sub.id)}
                      className={`w-full text-left px-4 py-2 rounded text-sm mb-1 flex items-center gap-2 ${activeSubMenu === sub.id
                          ? 'bg-purple-100 text-purple-800'
                          : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      <span className="text-xs">▸</span>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
              {item.id === 'packages' && activeTab === 'packages' && (
                <div className="ml-4 mt-1 mb-3">
                  {packagesSubMenus.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => navigate(`/paas-admin/${paasId}/${sub.id === 'all-packages' ? 'packages' : sub.id === 'add-package' ? 'add-package' : 'package-usage'}`)}
                      className={`w-full text-left px-4 py-2 rounded text-sm mb-1 flex items-center gap-2 ${activeSubMenu === sub.id
                          ? 'bg-purple-100 text-purple-800'
                          : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      <span className="text-xs">▸</span>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
              {item.id === 'analytics' && activeTab === 'analytics' && (
                <div className="ml-4 mt-1 mb-3">
                  {analyticsSubMenus.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubMenu(sub.id)}
                      className={`w-full text-left px-4 py-2 rounded text-sm mb-1 flex items-center gap-2 ${activeSubMenu === sub.id
                          ? 'bg-purple-100 text-purple-800'
                          : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                      <span className="text-xs">▸</span>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t" >
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded flex items-center gap-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

       {/* Main Content */}
       <div className="flex-1 flex flex-col overflow-hidden">
         {/* Top Header */}
         <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
           <div className="flex items-center gap-4">
             <h1 className="text-2xl font-bold text-gray-800">
               {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
             </h1>
           </div>
           <div className="flex items-center gap-4">
             <div className="text-sm text-gray-600">
               <span className="font-medium">{user?.email}</span>
               <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">PAAS Admin</span>
             </div>
             <button
               onClick={() => setShowTenantModal(true)}
               className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
             >
               + Create Client
             </button>
           </div>
         </header>

         {/* Scrollable Content */}
         <div className="flex-1 overflow-y-auto p-6">

        {/* Stats Bar */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Total API Calls</div>
              <div className="text-2xl font-bold">{saas?.analytics?.totalCalls?.toLocaleString() || '0'}</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Credits</div>
              <div className="text-2xl font-bold text-blue-600">{saas?.analytics?.credits?.toLocaleString() || '0'}</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Total Clients</div>
              <div className="text-2xl font-bold">{saasList.length}</div>
            </div>
            <div className="bg-white p-4 rounded shadow-sm">
              <div className="text-gray-500 text-sm">Active Clients</div>
              <div className="text-2xl font-bold">{saasList.filter(t => t.isActive).length}</div>
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="px-6 py-2 bg-white border-b flex gap-2 flex-wrap">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.hasSubMenu) {
                  setActiveTab(item.id);
                  setActiveSubMenu('all-saas');
                } else {
                  setActiveTab(item.id);
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === item.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {activeTab === 'saas' && (
          <div className="px-6 py-2 bg-gray-50 border-b flex gap-2">
            {saasSubMenus.map((sub) => (
              <button
                key={sub.id}
                onClick={() => setActiveSubMenu(sub.id)}
                className={`px-4 py-1.5 rounded text-sm font-medium ${activeSubMenu === sub.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {sub.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'overview' && (
          <>
            <StatsGrid>
              <StatCard title="Total Clients" value={saasList.length} change={20} />
              <StatCard title="Active Clients" value={saasList.filter(t => t.isActive).length} />
              <StatCard title="Total Users" value={saasList.reduce((sum, t) => sum + (t.analytics?.totalUsers || 0), 0)} change={12} />
              <StatCard title="Monthly Revenue" value={`$${saasList.reduce((sum, t) => sum + (t.subscription?.monthlyPrice || 0), 0)}`} />
            </StatsGrid>

            <AnalyticsGrid>
              <BarChartWidget title="Client Revenue" data={generateMockData(7)} xKey="date" yKey="revenue" />
              <PieChartWidget title="Client Status" data={[
                { name: 'Active', value: saasList.filter(t => t.isActive).length },
                { name: 'Suspended', value: saasList.filter(t => !t.isActive).length }
              ]} nameKey="name" valueKey="value" />
            </AnalyticsGrid>

            {/* Packages, Usage, Transactions, Income Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Packages</h3>
                {packagesLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : packages.length > 0 ? (
                  <PieChartWidget 
                    data={(() => {
                      const types = {};
                      packages.forEach(pkg => {
                        const type = pkg.type || 'other';
                        types[type] = (types[type] || 0) + 1;
                      });
                      return Object.entries(types).map(([name, value]) => ({ name, value }));
                    })()} 
                    nameKey="name" 
                    valueKey="value" 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No packages</p>
                )}
              </div>

              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Usage Stats</h3>
                {usageData ? (
                  <LineChartWidget 
                    title=""
                    data={usageData.dailyStats?.slice(-14).map(d => ({
                      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      apiCalls: d.apiCalls || 0,
                      users: d.users || 0
                    })) || generateMockData(14)} 
                    xKey="date" 
                    yKeys={['apiCalls', 'users']} 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No usage data</p>
                )}
              </div>

              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Credit Transactions</h3>
                {billingLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : transactions.length > 0 ? (
                  <LineChartWidget 
                    title=""
                    data={transactions.slice(-7).reverse().map(tx => ({
                      date: new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      amount: tx.amount || 0
                    }))} 
                    xKey="date" 
                    yKeys={['amount']} 
                  />
                ) : (
                  <p className="text-gray-500 text-center py-8">No transactions</p>
                )}
              </div>

              <div className="bg-white rounded shadow p-4">
                <h3 className="text-lg font-semibold mb-4">Income</h3>
                {incomeData ? (
                  <div>
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-green-700">{incomeData.total || 0} {incomeData.currency || 'INR'}</div>
                      <div className="text-sm text-gray-500">Total Income</div>
                    </div>
                    {incomeData.byPeriod && incomeData.byPeriod.length > 0 ? (
                      <BarChartWidget 
                        data={incomeData.byPeriod.map(p => ({
                          period: p.period || '',
                          income: p.income || 0
                        }))} 
                        xKey="period" 
                        yKey="income" 
                        height={200}
                      />
                    ) : null}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No income data</p>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'clients' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Total Clients</div>
                <div className="text-2xl font-bold">{saasList.length}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Active Clients</div>
                <div className="text-2xl font-bold">{saasList.filter(t => t.isActive).length}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Total Users</div>
                <div className="text-2xl font-bold">{saasList.reduce((sum, t) => sum + (t.analytics?.totalUsers || 0), 0)}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Monthly Revenue</div>
                <div className="text-2xl font-bold">${saasList.reduce((sum, t) => sum + (t.subscription?.monthlyPrice || 0), 0)}</div>
              </div>
            </div>

            <div className="bg-white rounded shadow overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Client Name</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Plan</th>
                    <th className="px-4 py-2 text-left">Users</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="px-4 py-4 text-center">Loading...</td></tr>
                  ) : saasList.length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-4 text-center">No clients. Create one to get started.</td></tr>
                  ) : (
                    saasList.map((tenant) => (
                      <tr key={tenant.tenantId} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium">{tenant.name}</div>
                        </td>
                        <td className="px-4 py-3">{tenant.contactEmail}</td>
                        <td className="px-4 py-3">{tenant.subscription?.plan}</td>
                        <td className="px-4 py-3">{tenant.analytics?.totalUsers || 0}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-sm ${tenant.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {tenant.isActive ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                             onClick={() => navigate(`/saas-admin/${tenant.saasId}`)}
                            className="text-blue-600 hover:underline mr-3"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => handleStatusToggle(tenant.tenantId, tenant.isActive)}
                            className="text-yellow-600 hover:underline mr-3"
                          >
                            {tenant.isActive ? 'Suspend' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteTenant(tenant.tenantId)}
                            className="text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'usage' && (
          <div className="p-6">
            <div className="bg-white rounded shadow p-6">
              <h2 className="text-xl font-bold mb-4">System Usage</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-gray-500 text-sm">Total API Calls</div>
                  <div className="text-3xl font-bold">{saas?.analytics?.totalCalls?.toLocaleString() || '0'}</div>
                  <div className="text-green-600 text-sm mt-1">↑ 8% from last month</div>
                </div>
                <div className="p-4 bg-blue-50 rounded border border-blue-200">
                  <div className="text-blue-600 text-sm">Credits</div>
                  <div className="text-3xl font-bold text-blue-700">{saas?.analytics?.credits?.toLocaleString() || '0'}</div>
                  <div className="text-blue-600 text-sm mt-1">available credits</div>
                </div>
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-gray-500 text-sm">Storage Used</div>
                  <div className="text-3xl font-bold">{saas?.analytics?.storageUsed || '0'} GB</div>
                  <div className="text-gray-500 text-sm mt-1">of {saas?.analytics?.storageLimit || '5'} GB available</div>
                </div>
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-gray-500 text-sm">Active Users</div>
                  <div className="text-3xl font-bold">{saasList.reduce((sum, t) => sum + (t.analytics?.totalUsers || 0), 0)}</div>
                  <div className="text-gray-500 text-sm mt-1">across all clients</div>
                </div>
                <div className="p-4 bg-gray-50 rounded">
                  <div className="text-gray-500 text-sm">Bandwidth</div>
                  <div className="text-3xl font-bold">{saas?.analytics?.bandwidth || '0'} GB</div>
                  <div className="text-gray-500 text-sm mt-1">this month</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="p-6">
            <div className="bg-white rounded shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Payments & Billing</h2>
                <button
                  onClick={() => setShowTopupModal(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  + Top Up Credits
                </button>
                <button onClick={() => navigate(`/paas-admin/${paasId}/credits`)} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">Credits</button>
                <button onClick={() => navigate(`/paas-admin/${paasId}/devices`)} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700">Devices</button>
              </div>
              {billingLoading ? (
                <div className="text-center py-8">Loading billing data...</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-6 mb-6">
                    <div className="p-4 bg-green-50 rounded border border-green-200">
                      <div className="text-green-600 text-sm">Available Credits</div>
                      <div className="text-3xl font-bold text-green-700">{balance?.available || 0} {balance?.currency || 'INR'}</div>
                      <div className="text-green-600 text-sm">Balance: {balance?.balance || 0}</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded border border-blue-200">
                      <div className="text-blue-600 text-sm">Spent</div>
                      <div className="text-3xl font-bold text-blue-700">{balance?.spent || 0}</div>
                      <div className="text-blue-600 text-sm">Total spent</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded border border-purple-200">
                      <div className="text-purple-600 text-sm">Active Subscriptions</div>
                      <div className="text-3xl font-bold text-purple-700">{saasList.filter(t => t.subscription?.plan !== 'free').length}</div>
                      <div className="text-purple-600 text-sm">Premium plans</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Recent Transactions</h3>
                    {transactions.length === 0 ? (
                      <p className="text-gray-500">No transactions yet.</p>
                    ) : (
                      <div className="bg-gray-50 rounded overflow-hidden">
                        <table className="min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">Type</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                              <th className="px-4 py-2 text-left">Description</th>
                              <th className="px-4 py-2 text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx) => (
                              <tr key={tx._id} className="border-t">
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {tx.type}
                                  </span>
                                </td>
                                <td className="px-4 py-2 font-medium">{tx.amount} {balance?.currency || 'INR'}</td>
                                <td className="px-4 py-2 text-sm">{tx.description || '-'}</td>
                                <td className="px-4 py-2 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Recent Invoices</h3>
                    {invoices.length === 0 ? (
                      <p className="text-gray-500">No invoices yet.</p>
                    ) : (
                      <div className="bg-gray-50 rounded overflow-hidden">
                        <table className="min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">Invoice #</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                              <th className="px-4 py-2 text-left">Status</th>
                              <th className="px-4 py-2 text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((inv) => (
                              <tr key={inv._id} className="border-t">
                                <td className="px-4 py-2">{inv.invoiceNumber}</td>
                                <td className="px-4 py-2 font-medium">{inv.total} {inv.currency}</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-1 rounded text-xs ${inv.status === 'paid' ? 'bg-green-100 text-green-800' : inv.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {inv.status}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm">{new Date(inv.createdAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'income' && (
          <div className="p-6">
            <div className="bg-white rounded shadow p-6">
              <h2 className="text-xl font-bold mb-4">Income Tracking</h2>
              <p className="text-sm text-gray-600 mb-4">Track payments from your clients (bottom-up flow)</p>
              <div className="mb-6">
                <button
                  onClick={fetchIncomeData}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Refresh Income Data
                </button>
              </div>
              {incomeLoading ? (
                <div className="text-center py-8">Loading income data...</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-6 mb-6">
                    <div className="p-4 bg-green-50 rounded border border-green-200">
                      <div className="text-green-600 text-sm">Total Income</div>
                      <div className="text-3xl font-bold text-green-700">{incomeData?.total || 0} {incomeData?.currency || 'INR'}</div>
                      <div className="text-green-600 text-sm">From all clients</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded border border-blue-200">
                      <div className="text-blue-600 text-sm">Active Clients</div>
                      <div className="text-3xl font-bold text-blue-700">{saasList.filter(t => t.isActive).length}</div>
                      <div className="text-blue-600 text-sm">Contributing</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded border border-purple-200">
                      <div className="text-purple-600 text-sm">Avg. per Client</div>
                      <div className="text-3xl font-bold text-purple-700">
                        {saasList.filter(t => t.isActive).length > 0 ? ((incomeData?.total || 0) / saasList.filter(t => t.isActive).length).toFixed(2) : 0}
                      </div>
                      <div className="text-purple-600 text-sm">{incomeData?.currency || 'INR'}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'transfer' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded shadow p-6">
                <h2 className="text-xl font-bold mb-4">Transfer Credits</h2>
                <p className="text-sm text-gray-600 mb-4">Transfer credits to your SaaS (top-down)</p>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Open Transfer Form
                </button>
              </div>
              <div className="bg-white rounded shadow p-6 lg:col-span-2">
                <h3 className="text-lg font-semibold mb-4">Recent Transfers</h3>
                {billingLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : transactions.filter(tx => tx.reference?.type === 'transfer').length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No transfers yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm">Date</th>
                          <th className="px-4 py-2 text-left text-sm">Direction</th>
                          <th className="px-4 py-2 text-left text-sm">To/From</th>
                          <th className="px-4 py-2 text-left text-sm">Amount</th>
                          <th className="px-4 py-2 text-left text-sm">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.filter(tx => tx.reference?.type === 'transfer').slice(0, 10).map((tx) => (
                          <tr key={tx._id} className="border-t">
                            <td className="px-4 py-2 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${tx.type === 'debit' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                {tx.type === 'debit' ? '→ TO' : '← FROM'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm font-medium">
                              {tx.type === 'debit' ? tx.reference?.toTenantId : tx.reference?.fromTenantId || '-'}
                            </td>
                            <td className={`px-4 py-2 font-medium ${tx.type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                              {tx.type === 'debit' ? `-₹${Math.abs(tx.amount)}` : `+₹${tx.amount}`}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{tx.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <CreditTransferModal
              isOpen={showTransferModal}
              onClose={() => setShowTransferModal(false)}
              mode="transfer"
              userRole="paasadmin"
              userContext={{ paasId: paasId }}
            />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">SaaS Settings</h2>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Edit Branding
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">System Name</p>
                <p className="font-medium">{saas?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Domain</p>
                <p className="font-medium">{saas?.domain || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Contact Email</p>
                <p className="font-medium">{saas?.contactEmail || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Primary Color</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded"
                    style={{ backgroundColor: saas?.primaryColor || '#7C3AED' }}
                  />
                  <span>{saas?.primaryColor || '#7C3AED'}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Terms URL</p>
                <p className="font-medium text-blue-600">{saas?.termsUrl || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Privacy URL</p>
                <p className="font-medium text-blue-600">{saas?.privacyUrl || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Support Email</p>
                <p className="font-medium">{saas?.supportEmail || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`px-2 py-1 rounded text-sm ${saas?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                  {saas?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'saas' && activeSubMenu === 'all-saas' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">All SaaS Systems</h2>
              <button
                onClick={() => { setActiveTab('saas'); setActiveSubMenu('create-saas'); }}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
              >
                + Create SaaS System
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Total SaaS Systems</div>
                <div className="text-2xl font-bold">{saasList.length}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Active</div>
                <div className="text-2xl font-bold">{saasList.filter(t => t.isActive).length}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Total Users</div>
                <div className="text-2xl font-bold">{saasList.reduce((sum, t) => sum + (t.analytics?.totalUsers || 0), 0)}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-gray-500">Monthly Revenue</div>
                <div className="text-2xl font-bold">${saasList.reduce((sum, t) => sum + (t.subscription?.monthlyPrice || 0), 0)}</div>
              </div>
            </div>

            <div className="bg-white rounded shadow overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Domain</th>
                    <th className="px-4 py-2 text-left">Plan</th>
                    <th className="px-4 py-2 text-left">Users</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="px-4 py-4 text-center">Loading...</td></tr>
                  ) : saasList.length === 0 ? (
                    <tr><td colSpan="7" className="px-4 py-4 text-center">No SaaS systems found. Create one to get started.</td></tr>
                  ) : (
                    saasList.map((tenant) => (
                      <tr key={tenant.tenantId || tenant.saasId} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium">{tenant.name}</div>
                        </td>
                        <td className="px-4 py-3">{tenant.contactEmail}</td>
                        <td className="px-4 py-3 text-sm">{tenant.domain || '-'}</td>
                        <td className="px-4 py-3">{tenant.subscription?.plan || 'free'}</td>
                        <td className="px-4 py-3">{tenant.analytics?.totalUsers || 0}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-sm ${tenant.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {tenant.isActive ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(`/saas-admin/${tenant.saasId}`)}
                            className="text-blue-600 hover:underline mr-3"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => handleStatusToggle(tenant.tenantId, tenant.isActive)}
                            className="text-yellow-600 hover:underline mr-3"
                          >
                            {tenant.isActive ? 'Suspend' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteTenant(tenant.tenantId)}
                            className="text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'saas' && activeSubMenu === 'create-saas' && (
          <div className="p-6">
            <div className="bg-white rounded shadow p-6 max-w-3xl">
              <h2 className="text-xl font-bold mb-4">Create New SaaS System</h2>
              <form onSubmit={handleCreateTenant}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Organization Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Email *</label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Support Email</label>
                    <input
                      type="email"
                      value={formData.supportEmail}
                      onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Domain</label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Plan</label>
                    <select
                      value={formData.subscription?.plan || 'free'}
                      onChange={(e) => setFormData({ ...formData, subscription: { ...formData.subscription, plan: e.target.value } })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Color</label>
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-full h-10 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Logo</label>
                    <div className="border-2 border-dashed rounded p-4 text-center cursor-pointer hover:border-gray-400" onClick={() => document.getElementById('saasLogoInput').click()}>
                      {formData.logoFilePreview ? (
                        <img src={formData.logoFilePreview} alt="Logo preview" className="h-16 object-contain mx-auto" />
                      ) : (
                        <div className="text-gray-500 text-sm">Click to upload logo</div>
                      )}
                    </div>
                    <input type="file" id="saasLogoInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    {formData.logoFilePreview && (
                      <button type="button" onClick={() => setFormData({ ...formData, logoFile: null, logoFilePreview: null })} className="text-red-500 text-xs mt-1">Remove</button>
                    )}
                  </div>
                  <div className="col-span-2">
                    <h3 className="text-md font-semibold mt-4 mb-2">SaaS Admin Account</h3>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Admin Email *</label>
                    <input
                      type="email"
                      value={formData.clientAdminEmail}
                      onChange={(e) => setFormData({ ...formData, clientAdminEmail: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Admin Password *</label>
                    <input
                      type="password"
                      value={formData.clientAdminPassword}
                      onChange={(e) => setFormData({ ...formData, clientAdminPassword: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div className="col-span-2 mt-4">
                    <button
                      type="submit"
                      className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700"
                    >
                      Create SaaS System
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'saas' && activeSubMenu === 'saas-admins' && (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-6">SaaS Admins</h2>
            {saasAdminsLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : saasAdmins.length === 0 ? (
              <div className="bg-white rounded shadow p-6 text-center">
                <p className="text-gray-500">No SaaS admins found.</p>
              </div>
            ) : (
              <div className="bg-white rounded shadow overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-left">SaaS System</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saasAdmins.map((admin) => (
                      <tr key={admin._id} className="border-t">
                        <td className="px-4 py-3">
                          <div className="font-medium">{admin.name || '-'}</div>
                        </td>
                        <td className="px-4 py-3">{admin.email}</td>
                        <td className="px-4 py-3">
                          {admin.saasId ? saasList.find(s => s.saasId === admin.saasId)?.name || admin.saasId : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-sm ${admin.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {admin.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="p-6">
            <div className="bg-white rounded shadow p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Billing Analytics</h2>
                <button
                  onClick={() => { fetchAnalyticsData(); fetchBillingData(); }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Refresh Data
                </button>
              </div>
              {analyticsLoading ? (
                <div className="text-center py-8">Loading analytics...</div>
              ) : (
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-green-50 rounded border border-green-200">
                    <div className="text-green-600 text-sm">Available Credits</div>
                    <div className="text-3xl font-bold text-green-700">{analyticsData?.available || balance?.available || 0}</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded border border-blue-200">
                    <div className="text-blue-600 text-sm">Income</div>
                    <div className="text-3xl font-bold text-blue-700">{analyticsData?.analytics?.income || 0}</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded border border-red-200">
                    <div className="text-red-600 text-sm">Expenses</div>
                    <div className="text-3xl font-bold text-red-700">{analyticsData?.analytics?.expenses || 0}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded border border-purple-200">
                    <div className="text-purple-600 text-sm">Transactions</div>
                    <div className="text-3xl font-bold text-purple-700">{analyticsData?.analytics?.transactionCount || 0}</div>
                  </div>
                </div>
              )}
            </div>

            {analyticsData?.analytics?.recentTransactions?.length > 0 && (
              <div className="bg-white rounded shadow p-6">
                <h3 className="text-lg font-semibold mb-3">Recent Transactions</h3>
                <div className="bg-gray-50 rounded overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Amount</th>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.analytics.recentTransactions.map((tx) => (
                        <tr key={tx._id} className="border-t">
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {tx.type}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-medium">{tx.amount} {analyticsData?.currency || 'INR'}</td>
                          <td className="px-4 py-2 text-sm">{tx.description || '-'}</td>
                          <td className="px-4 py-2 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {showTenantModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
            <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto my-6">
              <h2 className="text-xl font-bold mb-4">Create New Client (Organization)</h2>
              <form onSubmit={handleCreateTenant}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Organization Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Email *</label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Support Email</label>
                    <input
                      type="email"
                      value={formData.supportEmail}
                      onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Color</label>
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-full h-10 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Logo</label>
                    <div className="border-2 border-dashed rounded p-4 text-center cursor-pointer hover:border-gray-400" onClick={() => document.getElementById('logoFileInput').click()}>
                      {formData.logoFilePreview ? (
                        <img src={formData.logoFilePreview} alt="Logo preview" className="h-16 object-contain mx-auto" />
                      ) : (
                        <div className="text-gray-500 text-sm">Click to upload logo</div>
                      )}
                    </div>
                    <input type="file" id="logoFileInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    {formData.logoFilePreview && (
                      <button type="button" onClick={() => setFormData({ ...formData, logoFile: null, logoFilePreview: null })} className="text-red-500 text-xs mt-1">Remove</button>
                    )}
                     </div>
                   </div>
                 </form>
      </div>
    </div>
  )}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
            <h2 className="text-xl font-bold mb-4">Top Up Credits</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Credit Pack</label>
              <select id="saas-topup-amount" className="w-full border rounded px-3 py-2" value={formData.topupAmount || 5000} onChange={(e) => setFormData({...formData, topupAmount: e.target.value})}>
                <option value={500}>₹500 - Intro Pack</option>
                <option value={5000}>₹5,000 - Basic Pack</option>
                <option value={10000}>₹10,000 - Bronze Pack</option>
                <option value={50000}>₹50,000 - Silver Pack</option>
                <option value={100000}>₹100,000 - Gold Pack</option>
                <option value={1000000}>₹1,000,000 - Platinum Pack</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Payment Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className="p-3 border rounded text-center hover:bg-gray-50" onClick={() => handleTopup('razorpay')}>💳 Razorpay</button>
                <button type="button" className="p-3 border rounded text-center hover:bg-gray-50" onClick={() => handleTopup('stripe')}>💳 Stripe</button>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowTopupModal(false)} className="bg-gray-600 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
       </div>
     </div>
   </div>
      )
  };
